"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { amountLabel, formatCurrency } from "@/lib/utils";

interface PriceAdjustmentInputProps {
  /**
   * The adjustment as the user reads it: positive = surcharge (raises the
   * price), negative = discount (lowers it). Convert with toBackendDiscount
   * before sending to the API.
   */
  value: number;
  onChange: (value: number) => void;
  /** Disables the field entirely, e.g. once an invoice has been paid. */
  disabled?: boolean;
  /** Shown in place of the helper line to explain why the field is locked. */
  disabledReason?: string;
  /** When provided, renders a live "Final amount" preview of base + value. */
  basePrice?: number;
  /** Overrides the default "Price Adjustment (QAR)" label. */
  label?: string;
  id?: string;
}

export function PriceAdjustmentInput({
  value,
  onChange,
  disabled = false,
  disabledReason,
  basePrice,
  label,
  id = "price-adjustment",
}: PriceAdjustmentInputProps) {
  const current = Number.isFinite(value) ? value : 0;

  const step = (delta: number) => {
    // Round to 2dp so repeated stepping can't accumulate float drift.
    onChange(Math.round((current + delta) * 100) / 100);
  };

  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>{label ?? amountLabel("Price Adjustment")}</Label>
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0 font-bold text-lg select-none"
          onClick={() => step(-1)}
          disabled={disabled}
          title="Decrease price (Discount)"
        >
          -
        </Button>
        <Input
          id={id}
          type="number"
          step="0.01"
          placeholder="Enter price adjustment (+ / -)"
          // Blank rather than a pinned "0" so the field can be cleared.
          value={current === 0 ? "" : current}
          disabled={disabled}
          onChange={(e) => {
            const val = e.target.value;
            onChange(val === "" ? 0 : parseFloat(val) || 0);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0 font-bold text-lg select-none"
          onClick={() => step(1)}
          disabled={disabled}
          title="Increase price (Surcharge)"
        >
          +
        </Button>
      </div>

      {disabled && disabledReason ? (
        <p className="text-xs text-muted-foreground">{disabledReason}</p>
      ) : (
        current !== 0 && (
          <p className="text-xs font-medium">
            {current > 0 ? (
              <span className="text-amber-600 dark:text-amber-400">
                ➕ Surcharge: Price increased by {formatCurrency(current)}
              </span>
            ) : (
              <span className="text-emerald-600 dark:text-emerald-400">
                ➖ Discount: Price decreased by {formatCurrency(Math.abs(current))}
              </span>
            )}
          </p>
        )
      )}

      {basePrice !== undefined && current !== 0 && (
        <p className="text-sm text-muted-foreground">
          Final amount: {formatCurrency(Math.max(0, basePrice + current))}
        </p>
      )}
    </div>
  );
}
